import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  CreateEncounterClassificationInput,
  EncounterClassification,
} from '@/types/api';

/**
 * Hooks de classificações POR ATENDIMENTO (EncounterClassification).
 *
 * PROMPT 07a: o front agora persiste N classificações (1+) por type
 * (cid10/ciap2), com UMA marcada como `isPrimary` por type. As
 * classificações são gerenciadas via endpoints aninhados em
 * /api/consultas-clinicas/:id/classificacoes — independentes do
 * SoapNote.ciap2/cid (mantidos no back pra retrocompat).
 *
 * Regra de UX: quando adiciona a primeira classificação de um type,
 * ela vira primária automaticamente. As demais são secundárias até o
 * médico clicar pra promover.
 */

const KEY = (encounterId: string) =>
  ['clinical-encounter', encounterId, 'classifications'] as const;

/** Lista todas as classificações do atendimento. */
export function useEncounterClassifications(
  encounterId: string | null,
): UseQueryResult<EncounterClassification[], Error> {
  return useQuery({
    queryKey: ['clinical-encounter', encounterId, 'classifications'],
    queryFn: ({ signal }) =>
      api.get<EncounterClassification[]>(
        `/api/consultas-clinicas/${encounterId}/classificacoes`,
        { signal },
      ),
    enabled: !!encounterId,
    staleTime: 30_000,
  });
}

/** Adiciona (ou atualiza idempotente) uma classificação. */
export function useAddEncounterClassification(encounterId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateEncounterClassificationInput) =>
      api.post<EncounterClassification>(
        `/api/consultas-clinicas/${encounterId}/classificacoes`,
        input,
      ),
    onSuccess: (created) => {
      queryClient.setQueryData<EncounterClassification[]>(
        KEY(encounterId),
        (prev) => upsertClassificacao(prev, created),
      );
    },
  });
}

/** Marca uma classificação como primária (e desmarca outras do mesmo type). */
export function useSetPrimaryClassification(encounterId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (classificationId: string) =>
      api.patch<EncounterClassification>(
        `/api/consultas-clinicas/${encounterId}/classificacoes/${classificationId}/primary`,
        {},
      ),
    onSuccess: (updated) => {
      queryClient.setQueryData<EncounterClassification[]>(
        KEY(encounterId),
        (prev) => markPrimary(prev, updated),
      );
    },
  });
}

/** Remove uma classificação. */
export function useRemoveEncounterClassification(encounterId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (classificationId: string) =>
      api.delete<void>(
        `/api/consultas-clinicas/${encounterId}/classificacoes/${classificationId}`,
      ),
    onSuccess: (_void, classificationId) => {
      queryClient.setQueryData<EncounterClassification[]>(
        KEY(encounterId),
        (prev) => (prev ?? []).filter((c) => c.id !== classificationId),
      );
    },
  });
}

// --- helpers (puros, testáveis) ---

function upsertClassificacao(
  prev: EncounterClassification[] | undefined,
  created: EncounterClassification,
): EncounterClassification[] {
  const list = prev ?? [];
  const idx = list.findIndex((c) => c.id === created.id);
  if (idx >= 0) {
    const next = list.slice();
    next[idx] = created;
    return next;
  }
  return [created, ...list];
}

function markPrimary(
  prev: EncounterClassification[] | undefined,
  primary: EncounterClassification,
): EncounterClassification[] {
  return (prev ?? []).map((c) => {
    if (c.id === primary.id) return { ...c, isPrimary: true };
    if (c.type === primary.type) return { ...c, isPrimary: false };
    return c;
  });
}
